"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRouteMatcher = exports.authMiddleware = exports.DEFAULT_IGNORED_ROUTES = exports.DEFAULT_CONFIG_MATCHER = void 0;
const backend_1 = require("@clerk/backend");
const server_1 = require("next/server");
const utils_1 = require("../utils");
const debugLogger_1 = require("../utils/debugLogger");
const authenticateRequest_1 = require("./authenticateRequest");
const clerkClient_1 = require("./clerkClient");
const errors_1 = require("./errors");
const redirect_1 = require("./redirect");
const utils_2 = require("./utils");
/**
 * The default ideal matcher that excludes the _next directory (internals) and all static files,
 * but it will match the root route (/) and any routes that start with /api or /trpc.
 */
exports.DEFAULT_CONFIG_MATCHER = ['/((?!.*\\..*|_next).*)', '/', '/(api|trpc)(.*)'];
/**
 * Any routes matching this path will be ignored by the middleware.
 * This is the inverted version of DEFAULT_CONFIG_MATCHER.
 */
exports.DEFAULT_IGNORED_ROUTES = ['/((?!api|trpc))(_next|.+\\..+)(.*)'];
const authMiddleware = (...args) => {
    const [params = {}] = args;
    const { beforeAuth, afterAuth, publicRoutes, ignoredRoutes, ...options } = params;
    const isIgnoredRoute = (0, exports.createRouteMatcher)(ignoredRoutes || exports.DEFAULT_IGNORED_ROUTES);
    const isPublicRoute = (0, exports.createRouteMatcher)(withDefaultPublicRoutes(publicRoutes));
    const defaultAfterAuth = createDefaultAfterAuth(isPublicRoute);
    return (0, debugLogger_1.withLogger)('authMiddleware', logger => async (req, evt) => {
        if (options.debug) {
            logger.enable();
        }
        logger.debug('URL debug', { url: req.nextUrl.href, method: req.method, headers: (0, utils_1.stringifyHeaders)(req.headers) });
        logger.debug('Options debug', { ...options, beforeAuth: !!beforeAuth, afterAuth: !!afterAuth });
        if (isIgnoredRoute(req)) {
            logger.debug({ isIgnoredRoute: true });
            console.warn((0, errors_1.receivedRequestForIgnoredRoute)(req.nextUrl.href, JSON.stringify(exports.DEFAULT_CONFIG_MATCHER)));
            return (0, utils_1.setHeader)(server_1.NextResponse.next(), backend_1.constants.Headers.AuthReason, 'ignored-route');
        }
        const beforeAuthRes = await (beforeAuth && beforeAuth(req, evt));
        if (beforeAuthRes === false) {
            logger.debug('Before auth returned false, skipping');
            return (0, utils_1.setHeader)(server_1.NextResponse.next(), backend_1.constants.Headers.AuthReason, 'skip');
        }
        else if (beforeAuthRes && (0, utils_1.isRedirect)(beforeAuthRes)) {
            logger.debug('Before auth returned redirect, following redirect');
            return (0, utils_1.setHeader)(beforeAuthRes, backend_1.constants.Headers.AuthReason, 'redirect');
        }
        const requestState = await (0, authenticateRequest_1.authenticateRequest)(req, options);
        if (requestState.isUnknown) {
            logger.debug('authenticateRequest state is unknown', requestState);
            return (0, authenticateRequest_1.handleUnknownState)(requestState);
        }
        else if (requestState.isInterstitial) {
            logger.debug('authenticateRequest state is interstitial', requestState);
            return (0, authenticateRequest_1.handleInterstitialState)(requestState, options);
        }
        const auth = Object.assign(requestState.toAuth(), { isPublicRoute: isPublicRoute(req) });
        logger.debug(() => ({ auth: JSON.stringify(auth) }));
        const afterAuthRes = await (afterAuth || defaultAfterAuth)(auth, req, evt);
        const finalRes = (0, utils_1.mergeResponses)(beforeAuthRes, afterAuthRes) || server_1.NextResponse.next();
        logger.debug(() => ({ mergedHeaders: (0, utils_1.stringifyHeaders)(finalRes.headers) }));
        if ((0, utils_1.isRedirect)(finalRes)) {
            logger.debug('Final response is redirect, following redirect');
            return (0, utils_1.setHeader)(finalRes, backend_1.constants.Headers.AuthReason, 'redirect');
        }
        if (options.debug) {
            (0, utils_2.setRequestHeadersOnNextResponse)(finalRes, req, { [backend_1.constants.Headers.EnableDebug]: 'true' });
            logger.debug(`Added ${backend_1.constants.Headers.EnableDebug} on request`);
        }
        return (0, utils_2.decorateRequest)(req, finalRes, requestState);
    });
};
exports.authMiddleware = authMiddleware;
/**
 * Create a function that matches a request against the specified routes.
 * Precomputes the glob matchers for the public routes, so we don't have to
 * recompile the regular expressions on every request.
 */
const createRouteMatcher = (routes) => {
    if (typeof routes === 'function') {
        return (req) => routes(req);
    }
    const routePatterns = [routes || ''].flat().filter(Boolean);
    const matchers = precomputePathRegex(routePatterns);
    return (req) => matchers.some(matcher => matcher.test(req.nextUrl.pathname));
};
exports.createRouteMatcher = createRouteMatcher;
const createDefaultAfterAuth = (isPublicRoute) => {
    return (auth, req) => {
        if (!auth.userId && !isPublicRoute(req)) {
            return (0, redirect_1.redirectToSignIn)({ returnBackUrl: req.url });
        }
        return server_1.NextResponse.next();
    };
};
const precomputePathRegex = (patterns) => {
    return patterns.map(pattern => (pattern instanceof RegExp ? pattern : utils_1.paths.toRegexp(pattern)));
};
const matchRoutesStartingWith = (path) => {
    path = path.replace(/\/$/, '');
    return new RegExp(`^${path}(/.*)?$`);
};
const withDefaultPublicRoutes = (publicRoutes) => {
    if (typeof publicRoutes === 'function') {
        return publicRoutes;
    }
    const routes = [publicRoutes || ''].flat().filter(Boolean);
    if (clerkClient_1.SIGN_IN_URL) {
        routes.push(matchRoutesStartingWith(clerkClient_1.SIGN_IN_URL));
    }
    if (clerkClient_1.SIGN_UP_URL) {
        routes.push(matchRoutesStartingWith(clerkClient_1.SIGN_UP_URL));
    }
    return routes;
};
